import { Navigate } from 'react-router-dom';
import { Suspense, useCallback } from 'react';
import {
  ContractDocumentPathParams,
  ContractDrugAnalyticsPathParams,
  ContractMarketingOrgPathParams,
  ContractPathParams,
  DorimPromoPaths,
  PharmacistsControlDetailPathParams,
  PharmacistsControlDetailReceiptInfoPathParams,
  PlanogramTaskControlDetailPathParams,
  SpecificationDocumentPathParams,
} from '@/router/paths/dorim-promo.path';
import { ProgressBar } from '@/components/ProgressBar';
import PromoProductsPage from '@/pages/promo';
import ContractTemplatesPage from '@/pages/contract-templates';
import { PromoContractsPage } from '@/pages/promo-contracts';
import { MarketingOrgAgreementPage } from '@/pages/promo-contracts/marketing-org-agreement';
import { ContractDocumentPage } from '@/pages/promo-contracts/document/contract-document';
import { ContractDrugAnalyticsPage } from '@/pages/promo-contracts/analytics';
import { usePromoContract } from '@/hooks/usePromoContract';
import { BreadcrumbLink, LazyBreadcrumb, RouteObjectWithBreadcrumbs } from '@/components/Breadcrumbs';
import { useMarketingOrgAgreement } from '@/hooks/useMarketingOrgAgreement';
import { MarketingOrgAgreementResponse } from '@/api/domains/promo-contracts.api';
import { formatDrugName } from '@/helpers/format.helpers';
import { ContractsLayout } from '@/layouts/DorimPromo';
import { usePlanogramTask } from '@/pages/planogram-task/model/usePlanogramTask';
import { PlanogramTasksControlPage } from '@/pages/planogram-tasks-control';
import { PharmacistsControlPage } from '@/pages/pharmacists-control-root';
import { Layout } from '@/pages/pharmacists-control-root/detail';
import { usePharmacist } from '@/pages/pharmacists-control-root/detail/usePharmacist';

import ReceiptInfoPage from '@/pages/pharmacists-control-root/detail/receipt-info/ReceiptInfoPage';
import { useReceiptInfo } from '@/pages/pharmacists-control-root/detail/receipt-info/useReceiptInfo';
import { SpecificationDocumentPage } from '@/pages/promo-contracts/document/specification-document';
import { ReceiptsPage } from '@/pages/pharmacists-control-root/detail/receipts';
import { PromoContractPageRoute } from '@/pages/promo-contracts/contract/promo-contract.route';
import { SalesPlanProductsPageRoute } from '@/pages/promo-contracts/contract/sales-plan-products';
import { PlanogramTaskPageRoute } from '@/pages/promo-contracts/contract/planogram-task';

export const DorimPromoRoute: RouteObjectWithBreadcrumbs = {
  path: DorimPromoPaths.ROOT_PATH,
  handle: {
    breadcrumb: {
      Component: () => <BreadcrumbLink to={DorimPromoPaths.ROOT_PATH}>Dorim.Promo</BreadcrumbLink>,
    },
  },
  children: [
    {
      index: true,
      element: <Navigate replace to={DorimPromoPaths.CONTRACT_TEMPLATES_PATH} />,
    },
    {
      path: DorimPromoPaths.PROMO_PRODUCTS_PATH,
      element: (
        <Suspense fallback={<ProgressBar />}>
          <PromoProductsPage />
        </Suspense>
      ),
    },
    {
      path: DorimPromoPaths.CONTRACT_TEMPLATES_PATH,
      element: (
        <Suspense fallback={<ProgressBar />}>
          <ContractTemplatesPage />
        </Suspense>
      ),
    },
    {
      path: DorimPromoPaths.CONTRACTS_PATH,
      element: <ContractsLayout />,
      handle: {
        breadcrumb: {
          Component: () => (
            <BreadcrumbLink to={DorimPromoPaths.CONTRACTS_PATH}>Спецификации с контрагентами</BreadcrumbLink>
          ),
        },
      },
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<ProgressBar />}>
              <PromoContractsPage />
            </Suspense>
          ),
        },
        {
          path: DorimPromoPaths.CONTRACT_PATH,
          handle: {
            breadcrumb: (params: ContractPathParams) => ({
              Component: () => {
                const { data, isLoading } = usePromoContract({
                  contractId: Number(params.contractId),
                });

                return (
                  <LazyBreadcrumb isLoading={isLoading}>
                    <BreadcrumbLink to={DorimPromoPaths.generateContractPath(params)}>
                      {data?.agreement?.document_number || 'Спецификация'}
                    </BreadcrumbLink>
                  </LazyBreadcrumb>
                );
              },
            }),
          },
          children: [
            PromoContractPageRoute,
            {
              path: DorimPromoPaths.CONTRACT_DOCUMENT_PATH,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <ContractDocumentPage />
                </Suspense>
              ),
              handle: {
                breadcrumb: (params: ContractDocumentPathParams) => ({
                  Component: () => (
                    <BreadcrumbLink to={DorimPromoPaths.generateContractDocumentPath(params)}>Договор</BreadcrumbLink>
                  ),
                }),
              },
            },
            {
              path: DorimPromoPaths.SPECIFICATION_DOCUMENT_PATH,
              element: (
                <Suspense fallback={<ProgressBar />}>
                  <SpecificationDocumentPage />
                </Suspense>
              ),
              handle: {
                breadcrumb: (params: SpecificationDocumentPathParams) => ({
                  Component: () => (
                    <BreadcrumbLink to={DorimPromoPaths.generateSpecificationDocumentPath(params)}>
                      Спецификация
                    </BreadcrumbLink>
                  ),
                }),
              },
            },
            SalesPlanProductsPageRoute,
            PlanogramTaskPageRoute,
            {
              path: DorimPromoPaths.MARKETING_ORG_PATH,
              handle: {
                breadcrumb: (params: ContractMarketingOrgPathParams) => ({
                  Component: () => {
                    const { data, isLoading } = useMarketingOrgAgreement({
                      agreementId: Number(params.contractId),
                      marketingOrgId: Number(params.marketingOrgId),
                    });

                    return (
                      <LazyBreadcrumb isLoading={isLoading}>
                        <BreadcrumbLink to={DorimPromoPaths.generateMarketingOrgPath(params)}>
                          {data?.marketing_org_details?.marketing_org?.name || 'Маркетирующая организация'}
                        </BreadcrumbLink>
                      </LazyBreadcrumb>
                    );
                  },
                }),
              },
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<ProgressBar />}>
                      <MarketingOrgAgreementPage />
                    </Suspense>
                  ),
                },
                {
                  path: DorimPromoPaths.ANALYTICS_PATH,
                  handle: {
                    breadcrumb: (params: ContractDrugAnalyticsPathParams) => ({
                      Component: () => {
                        const { data: analyticsDrug, isLoading } = useMarketingOrgAgreement({
                          agreementId: Number(params.contractId),
                          marketingOrgId: Number(params.marketingOrgId),
                          select: useCallback((data: MarketingOrgAgreementResponse | undefined) => {
                            return data?.marketing_org_details?.cashback_drugs?.find(
                              drug => drug.drug_id === Number(params.drugId),
                            );
                          }, []),
                        });

                        const breadcrumb = (title: string) => (
                          <LazyBreadcrumb isLoading={isLoading}>
                            <BreadcrumbLink to={DorimPromoPaths.generateContractPath(params)}>{title}</BreadcrumbLink>
                          </LazyBreadcrumb>
                        );

                        if (!analyticsDrug || !analyticsDrug.drug) {
                          return breadcrumb('Аналитика');
                        }

                        const { name, form, number } = analyticsDrug.drug;

                        return breadcrumb(formatDrugName(name, form, number));
                      },
                    }),
                  },
                  element: (
                    <Suspense fallback={<ProgressBar />}>
                      <ContractDrugAnalyticsPage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: DorimPromoPaths.CONTRACT_DOCUMENT_PATH,
      element: (
        <Suspense fallback={<ProgressBar />}>
          <ContractDocumentPage />
        </Suspense>
      ),
    },
    {
      path: DorimPromoPaths.PLANOGRAM_TASKS_CONTROL,
      handle: {
        breadcrumb: {
          Component: () => (
            <BreadcrumbLink to={DorimPromoPaths.PLANOGRAM_TASKS_CONTROL}>
              Контроль заданий по фотофиксации
            </BreadcrumbLink>
          ),
        },
      },
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<ProgressBar />}>
              <PlanogramTasksControlPage />
            </Suspense>
          ),
        },
        {
          path: DorimPromoPaths.PLANOGRAM_TASK_CONTROL_DETAIL_PATH,
          lazy: async () => {
            const { PlanogramTaskPage } = await import(
              /* webpackChunkName: "PlanogramTaskPage" */
              '@/pages/planogram-task'
            );

            return {
              Component: () => (
                <Suspense fallback={<ProgressBar />}>
                  <PlanogramTaskPage />
                </Suspense>
              ),
            };
          },
          handle: {
            breadcrumb: (params: PlanogramTaskControlDetailPathParams) => ({
              Component: () => {
                const { data: task } = usePlanogramTask({
                  taskId: Number(params.taskId),
                });

                return (
                  <BreadcrumbLink to={DorimPromoPaths.PLANOGRAM_TASK_CONTROL_DETAIL_PATH}>
                    {task?.name || 'Задание'}
                  </BreadcrumbLink>
                );
              },
            }),
          },
        },
      ],
    },
    {
      path: DorimPromoPaths.PHARMACISTS_CONTROL_PATH,
      handle: {
        breadcrumb: {
          Component: () => (
            <BreadcrumbLink to={DorimPromoPaths.PHARMACISTS_CONTROL_PATH}>Контроль провизоров</BreadcrumbLink>
          ),
        },
      },
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<ProgressBar />}>
              <PharmacistsControlPage />
            </Suspense>
          ),
        },
        {
          path: DorimPromoPaths.PHARMACISTS_CONTROL_DETAIL_PATH,
          element: <Layout />,
          children: [
            {
              index: true,
              element: <Navigate replace to={DorimPromoPaths.PHARMACISTS_CONTROL_PATH} />,
            },
            {
              path: DorimPromoPaths.PHARMACISTS_CONTROL_DETAIL_RECEIPTS_PATH,
              handle: {
                breadcrumb: (params: PharmacistsControlDetailPathParams) => ({
                  Component: () => {
                    const { data: pharmacist, isLoading } = usePharmacist({
                      pharmacistId: Number(params.pharmacistId),
                    });

                    return (
                      <LazyBreadcrumb isLoading={isLoading}>
                        <BreadcrumbLink
                          to={DorimPromoPaths.generatePharmacistsControlDetailReceiptsPath({
                            pharmacistId: params.pharmacistId,
                          })}
                        >
                          {`${pharmacist?.first_name} ${pharmacist?.last_name}`}
                        </BreadcrumbLink>
                      </LazyBreadcrumb>
                    );
                  },
                }),
              },
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<ProgressBar />}>
                      <ReceiptsPage />
                    </Suspense>
                  ),
                },
                {
                  path: DorimPromoPaths.PHARMACISTS_CONTROL_DETAIL_RECEIPT_INFO_PATH,
                  element: (
                    <Suspense fallback={<ProgressBar />}>
                      <ReceiptInfoPage />
                    </Suspense>
                  ),
                  handle: {
                    breadcrumb: (params: PharmacistsControlDetailReceiptInfoPathParams) => ({
                      Component: () => {
                        const { isFetching, data: receipt } = useReceiptInfo({ receiptId: params.receiptId });

                        return (
                          <LazyBreadcrumb isLoading={isFetching}>
                            <BreadcrumbLink
                              to={DorimPromoPaths.generatePharmacistsControlDetailReceiptInfoPath({
                                pharmacistId: params.pharmacistId,
                                receiptId: params.receiptId,
                              })}
                            >
                              №{receipt?.number}
                            </BreadcrumbLink>
                          </LazyBreadcrumb>
                        );
                      },
                    }),
                  },
                },
              ],
            },
            {
              path: DorimPromoPaths.PHARMACISTS_CONTROL_DETAIL_WALLET_PATH,
              lazy: async () => {
                const { WalletPage } = await import(
                  /* webpackChunkName: "WalletPage" */
                  '@/pages/pharmacists-control-root/detail/wallet'
                );
                return {
                  Component: () => (
                    <Suspense fallback={<ProgressBar />}>
                      <WalletPage />
                    </Suspense>
                  ),
                };
              },
              handle: {
                breadcrumb: (params: PharmacistsControlDetailPathParams) => ({
                  Component: () => {
                    const { data: pharmacist, isLoading } = usePharmacist({
                      pharmacistId: Number(params.pharmacistId),
                    });

                    return (
                      <LazyBreadcrumb isLoading={isLoading}>
                        <BreadcrumbLink
                          to={DorimPromoPaths.generatePharmacistsControlDetailReceiptsPath({
                            pharmacistId: params.pharmacistId,
                          })}
                        >
                          {`${pharmacist?.first_name} ${pharmacist?.last_name}`}
                        </BreadcrumbLink>
                      </LazyBreadcrumb>
                    );
                  },
                }),
              },
            },
          ],
        },
      ],
    },
  ],
};
